import { useEffect, useState, useRef } from 'react';
import CalcHexFromRal from '../function/CalcHexFromRAL';
import DropdownPortal from './DropdownPortal';
import InfoButton from '../components/InfoButton';

function CustomSelect({
  value,
  onChange,
  disabled,
  optionChoice,
  warning = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef(null);

  const normalizedChoices = Array.isArray(optionChoice) ? optionChoice : [];
  const shouldShowSearch = normalizedChoices.length > 10;

  const filteredOptions = shouldShowSearch
    ? normalizedChoices.filter((option) => {
        const optionText = String(
          typeof option === 'object' ? option.name || option.type : option
        ).toLowerCase();
        return optionText.includes(searchTerm.trim().toLowerCase());
      })
    : normalizedChoices;

  const handleOptionSelection = (optionValue) => {
    setIsOpen(false);
    onChange(optionValue);
  };

  const selectedOption = normalizedChoices.find(
    (option) =>
      String(typeof option === 'object' ? option.id : option) === String(value)
  );

  const rawDisplayText =
    selectedOption !== null && selectedOption !== undefined
      ? typeof selectedOption === 'object'
        ? selectedOption.name || selectedOption.type
        : selectedOption
      : 'Select an option';

  const displayText = roundIfNumeric(rawDisplayText, 2);

  const openDropdown = () => {
    if (!selectRef.current) return;

    const rect = selectRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    });

    setSearchTerm('');
    setIsOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target) &&
        isOpen
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside, {
      passive: true,
    });
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, [isOpen]);

  return disabled ? (
    <div className="result-text">{displayText}</div>
  ) : (
    <div
      className="select"
      ref={selectRef}
      style={
        displayText?.toString().includes('RAL')
          ? {
              backgroundColor: CalcHexFromRal(displayText).background,
              color: CalcHexFromRal(displayText).color,
            }
          : {}
      }
      onPointerDown={(e) => {
        if (!isOpen) {
          e.preventDefault();
          openDropdown();
        }
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="select-selected">
        <div className="select-text">{displayText}</div>

        {warning !== '' && (
          <InfoButton infoText={warning} type="warning" align="right" />
        )}
        <div className="arrow-container-select">
          <div className="arrow up"></div>
        </div>
      </div>

      {isOpen && coords && (
        <DropdownPortal>
          <div
            className="options"
            onPointerDown={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: coords.top,
              left: coords.left,
              width: coords.width,
              zIndex: 999999,
            }}
          >
            {shouldShowSearch && (
              <div className="option-search-row">
                <input
                  type="text"
                  className="option-search"
                  placeholder="Search options"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            {filteredOptions.map((option, index) => {
              const optionValue =
                typeof option === 'object' ? option.id : option;
              const optionDisplayText = roundIfNumeric(
                typeof option === 'object' ? option.name || option.type : option
              );
              return (
                <div
                  className={`option ${value === optionValue && 'active'}`}
                  key={index}
                  role="option"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOptionSelection(optionValue);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  style={
                    optionDisplayText?.toString().includes('RAL')
                      ? {
                          backgroundColor:
                            CalcHexFromRal(optionDisplayText).background,
                          color: CalcHexFromRal(optionDisplayText).color,
                        }
                      : {}
                  }
                >
                  {optionDisplayText}
                </div>
              );
            })}

            {filteredOptions.length === 0 && (
              <div className="option option-empty">No matching options</div>
            )}
          </div>
        </DropdownPortal>
      )}
    </div>
  );
}

export default CustomSelect;

function roundIfNumeric(val, decimals = 2) {
  const num =
    typeof val === 'number'
      ? val
      : typeof val === 'string' && val.trim() !== ''
        ? Number(val)
        : NaN;

  if (Number.isNaN(num)) return val;

  return Number(num.toFixed(decimals));
}
